import { useEffect, useState } from 'react'
import { Globe, Loader2, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCompany, useUpdateCompany } from '@/api/company'
import { useProfile } from '@/api/profile'
import { TIMEZONE_CHOICES } from '@/components/manager/company/timezones'

/**
 * Réglage du fuseau horaire de l'entreprise, affiché dans la page Profil
 * pour les MANAGERS uniquement.
 *
 * Important : ce n'est PAS la timezone personnelle de l'utilisateur — c'est
 * le fuseau opérationnel de la company (utilisé par les heures d'ouverture,
 * l'IVR `time_check`, la création de slots RDV, les AI agents schedule).
 * D'où le label explicite "Fuseau de l'entreprise".
 *
 * Section silencieuse pour les agents (rien à afficher) et pour les managers
 * sans company encore créée.
 */
export function CompanyTimezoneSection({ darkMode }: { darkMode: boolean }) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const isManager = profile?.role === 'MANAGER'
  const companyId = profile?.company

  const { data: company, isLoading } = useCompany(
    isManager ? companyId : undefined,
  )
  const updateCompany = useUpdateCompany(companyId)

  const [value, setValue] = useState<string>('America/Toronto')

  useEffect(() => {
    if (company?.timezone) setValue(company.timezone)
  }, [company?.timezone])

  if (!isManager || !companyId) return null

  const isDirty = value !== (company?.timezone || 'America/Toronto')

  const handleSave = async () => {
    try {
      await updateCompany.mutateAsync({ timezone: value })
      toast.success(
        t('profile.companyTimezoneSaved', 'Fuseau de l\'entreprise mis à jour'),
      )
    } catch {
      toast.error(
        t('profile.errors.companyTimezoneSave', 'Erreur lors de la sauvegarde'),
      )
    }
  }

  return (
    <section
      className={cn(
        'rounded-xl border p-5',
        darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <Globe className="h-4 w-4 text-indigo-500" />
        <h2
          className={cn(
            'text-sm font-semibold',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {t('profile.sections.companyTimezone', "Fuseau de l'entreprise")}
        </h2>
      </div>
      <p
        className={cn(
          'text-xs mb-4',
          darkMode ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {t(
          'profile.companyTimezoneHint',
          "Utilisé pour les heures d'ouverture, la prise de rendez-vous et les agents IA. S'applique à toute l'équipe.",
        )}
      </p>

      {isLoading ? (
        <div className="py-4 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5 max-w-md">
            <Label className="text-xs font-medium">
              {t('profile.companyTimezoneLabel', 'Fuseau horaire')}
            </Label>
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_CHOICES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isDirty && (
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setValue(company?.timezone || 'America/Toronto')}
                disabled={updateCompany.isPending}
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void handleSave()}
                disabled={updateCompany.isPending}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {updateCompany.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('profile.save', 'Sauvegarder')}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
